type IndustrialCaseMediaKind = 'audio' | 'video';

type IndustrialCaseRichTextSegment =
  | {
      type: 'html';
      html: string;
      key: string;
    }
  | {
      type: 'media';
      caption: string;
      key: string;
      mediaKind: IndustrialCaseMediaKind;
      src: string;
    };

const mediaFigurePattern =
  /<figure\b([^>]*\bdata-case-media\s*=\s*(?:"(?:audio|video)"|'(?:audio|video)')[^>]*)>[\s\S]*?<\/figure\s*>/gi;

/**
 * ### parseIndustrialCaseRichText
 *
 * Splits CKEditor HTML at customer-case media figures so prose and playable
 * media render in one synchronous React tree. Unknown figures stay inside the
 * HTML segments and continue through the standard rich-text renderer.
 *
 * @param html Authored narrative HTML emitted by CKEditor
 * @returns Ordered prose and media segments for the article renderer
 */
export function parseIndustrialCaseRichText(
  html: string,
): Array<IndustrialCaseRichTextSegment> {
  const segments: Array<IndustrialCaseRichTextSegment> = [];
  let cursor = 0;

  for (const match of html.matchAll(mediaFigurePattern)) {
    const figureHtml = match[0];
    const attributes = match[1];
    const start = match.index;
    if (attributes === undefined || start === undefined) continue;

    appendHtmlSegment(segments, html.slice(cursor, start), cursor);

    const mediaKind = readMediaKind(
      readHtmlAttribute(attributes, 'data-case-media'),
    );
    segments.push({
      type: 'media',
      caption:
        readHtmlAttribute(attributes, 'data-caption') ||
        readFigureCaption(figureHtml),
      key: `media-${start}-${mediaKind}`,
      mediaKind,
      src: readHtmlAttribute(attributes, 'data-src'),
    });
    cursor = start + figureHtml.length;
  }

  appendHtmlSegment(segments, html.slice(cursor), cursor);
  return segments;
}

function appendHtmlSegment(
  segments: Array<IndustrialCaseRichTextSegment>,
  html: string,
  start: number,
) {
  if (!html) return;
  segments.push({ type: 'html', html, key: `html-${start}` });
}

function readHtmlAttribute(attributes: string, name: string) {
  const pattern = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i');
  const match = pattern.exec(attributes);
  return decodeHtmlAttribute(match?.[1] ?? match?.[2] ?? '');
}

function readFigureCaption(figureHtml: string) {
  const captionHtml =
    /<figcaption\b[^>]*>([\s\S]*?)<\/figcaption\s*>/i.exec(figureHtml)?.[1] ??
    '';
  return decodeHtmlAttribute(
    captionHtml
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim(),
  );
}

function decodeHtmlAttribute(value: string) {
  return value.replace(
    /&(?:#(\d+)|#x([\da-f]+)|(amp|apos|gt|lt|nbsp|quot));/gi,
    (entity, decimal: string, hexadecimal: string, named: string) => {
      if (decimal) return String.fromCodePoint(Number(decimal));
      if (hexadecimal) return String.fromCodePoint(parseInt(hexadecimal, 16));

      return (
        {
          amp: '&',
          apos: "'",
          gt: '>',
          lt: '<',
          nbsp: ' ',
          quot: '"',
        }[named.toLowerCase()] ?? entity
      );
    },
  );
}

function readMediaKind(value: string): IndustrialCaseMediaKind {
  return value === 'video' ? 'video' : 'audio';
}
